import { Link, useNavigate, useParams } from "react-router-dom";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaGrid from "@/components/feature/MediaGrid";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeader from "@/components/feature/SectionHeader";
import ShareButton from "@/components/feature/ShareButton";
import {
  getMediaById,
  getRelatedMedia,
  categoryLabel,
  formatViews,
} from "@/lib/media";
import { getNewsByMedia } from "@/lib/news";

export default function Detail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const item = getMediaById(id);

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-20">
        <PageMeta title="内容不存在-蜜桃成熟" />
        <EmptyState
          icon="ri-film-line"
          title="没有找到该影视作品"
          description="它可能已被移动或链接有误，返回首页继续浏览其他作品吧。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  const related = getRelatedMedia(item, 8);
  const relatedNews = getNewsByMedia(item.id);
  const backTo = item.type === "tv" ? "/category/tv" : "/category/movie";

  const metaRows = [
    { label: "上映年份", value: `${item.year}` },
    { label: "地区", value: item.region },
    { label: "导演", value: item.director },
    { label: "类型", value: item.genres.join(" / ") },
  ];

  return (
    <>
      <PageMeta
        title={`《${item.title}》详细介绍 - 蜜桃成熟`}
        description={`《${item.title}》是一部${item.year}年${item.region}${categoryLabel(item.type)}，类型为${item.genres.join("、")}。${item.synopsis}`}
      />

      <section className="relative w-full bg-foreground-950">
        <div className="absolute inset-0">
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} 详情`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/80 to-foreground-950/60"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 py-8 md:py-12">
          <div className="mb-6 [&_a]:text-background-100/80 [&_span]:text-background-50/70 [&_i]:text-background-50/40">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: categoryLabel(item.type), to: backTo },
                { label: item.title },
              ]}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-40 md:w-56 shrink-0 rounded-lg overflow-hidden bg-background-800 mx-auto md:mx-0">
              <img
                src={item.cover}
                alt={`${item.title} 封面`}
                title={`${item.title} 海报`}
                className="w-full aspect-[2/3] object-cover object-top"
              />
            </div>

            <div className="flex-1 min-w-0 text-background-50">
              <h1 className="text-2xl md:text-4xl font-heading font-semibold leading-tight">
                {item.title}
              </h1>

              <div className="flex items-center flex-wrap gap-3 mt-4 text-sm">
                <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50">
                  {categoryLabel(item.type)}
                </span>
                <span className="flex items-center gap-1 text-accent-300">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill"></i>
                  </span>
                  <span className="text-background-50">{item.rating.toFixed(1)}</span>
                </span>
                <span className="flex items-center gap-1 text-background-100/80">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-fire-line"></i>
                  </span>
                  {formatViews(item.views)} 浏览
                </span>
                <span className="text-background-100/80">
                  {item.year} · {item.region}
                </span>
              </div>

              <p className="text-background-100/85 leading-relaxed mt-5 text-sm md:text-base max-w-3xl">
                {item.synopsis}
              </p>

              <div className="flex flex-wrap gap-2 mt-5">
                {item.genres.map((genre) => (
                  <span
                    key={genre}
                    className="px-3 py-1 rounded-full text-xs border border-background-50/30 text-background-50/90"
                  >
                    {genre}
                  </span>
                ))}
              </div>

              <div className="flex items-center flex-wrap gap-3 mt-7">
                <button
                  type="button"
                  onClick={() => navigate(backTo)}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm whitespace-nowrap transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </span>
                  返回列表
                </button>
                <ShareButton title={`《${item.title}》- 蜜桃成熟`} />
                <span className="text-xs text-background-100/60 border border-background-50/20 rounded-md px-3 py-2">
                  演示站点 · 不提供在线播放
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-10 md:py-14 space-y-12 md:space-y-16">
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <SectionHeader title="剧情简介" />
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                {item.description}
              </p>
            </div>
          </div>

          <div>
            <SectionHeader title="详细信息" />
            <div className="rounded-lg border border-background-200 bg-background-50 divide-y divide-background-200">
              {metaRows.map((row) => (
                <div key={row.label} className="flex items-start gap-4 px-5 py-4 text-sm">
                  <span className="text-foreground-400 w-20 shrink-0">{row.label}</span>
                  <span className="text-foreground-800">{row.value}</span>
                </div>
              ))}
              <div className="flex items-start gap-4 px-5 py-4 text-sm">
                <span className="text-foreground-400 w-20 shrink-0">主演</span>
                <span className="text-foreground-800">{item.cast.join("、")}</span>
              </div>
            </div>
          </div>
        </section>

        <section>
          <SectionHeader title="相关资讯" subtitle="与这部作品有关的报道与动态" moreTo="/news" />
          {relatedNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {relatedNews.map((news) => (
                <Link
                  key={news.id}
                  to={`/news/detail/${news.id}`}
                  className="group flex gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                >
                  <div className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-100">
                    <img
                      src={news.image}
                      alt={news.title}
                      title={`${news.title} 资讯`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm text-foreground-900 font-medium leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {news.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{news.publishTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-5 py-8 text-center text-sm text-foreground-500">
              暂无与该作品直接相关的资讯，去
              <Link to="/news" className="text-primary-600 hover:underline mx-1">
                资讯频道
              </Link>
              看看其他内容。
            </div>
          )}
        </section>

        <section>
          <SectionHeader
            title="相关推荐"
            subtitle={`同为${categoryLabel(item.type)}或题材相近的作品`}
            moreTo={backTo}
            accent
          />
          <MediaGrid items={related} />
        </section>
      </div>
    </>
  );
}